import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
  FLOOR_PLAN_SCHEMA_VERSION,
  FloorTable,
  Room,
  TableShape
} from '../types/floorPlan.model'
import {
  createTable,
  DEFAULT_DIMENSIONS,
  DEFAULT_ROOMS,
  findFreeTablePosition,
  getDimensionsForShape,
  getNextTableLabel,
  ZOOM_MAX,
  ZOOM_MIN
} from '../utils/defaults'

type TableTransform = Partial<
  Pick<FloorTable, 'x' | 'y' | 'width' | 'height' | 'rotation'>
>

// One reversible step: the data fields that undo/redo restore.
type HistorySnapshot = Pick<
  FloorPlanState,
  'rooms' | 'tables' | 'activeRoomId'
>

const HISTORY_LIMIT = 50

interface FloorPlanState {
  rooms: Room[]
  tables: FloorTable[]
  activeRoomId: string | null
  // Serialized form of the last plan saved to (or loaded from) the backend.
  // Compared against the current plan to know whether there are unsaved edits.
  // Not persisted, a reload re-establishes it from the backend on hydration.
  savedSnapshot: string | null
  setSavedSnapshot: (snapshot: string | null) => void
  snapEnabled: boolean
  zoom: number
  // Active canvas tool: 'select' (cursor) or 'hand' (pan the board).
  tool: 'select' | 'hand'
  setTool: (tool: 'select' | 'hand') => void
  // Pan offset of the board (screen px) and a "centre on the tables" action.
  pan: { x: number; y: number }
  setPan: (pan: { x: number; y: number }) => void
  fitToTables: () => void
  // Saved pan + zoom per room, so switching rooms restores that room's view
  // instead of carrying the current scroll position across rooms.
  viewByRoom: Record<string, { pan: { x: number; y: number }; zoom: number }>
  // Live canvas size (px), set by the canvas, used to place new tables.
  canvasWidth: number
  canvasHeight: number
  setCanvasSize: (width: number, height: number) => void

  // undo / redo history (not persisted)
  past: HistorySnapshot[]
  future: HistorySnapshot[]
  undo: () => void
  redo: () => void

  // grid snapping (position + 15° rotation)
  toggleSnap: () => void

  // whole-planner zoom
  setZoom: (zoom: number) => void

  // rooms
  addRoom: (name: string) => void
  renameRoom: (id: string, name: string) => void
  deleteRoom: (id: string) => void
  setActiveRoom: (id: string) => void

  // tables
  addTable: (shape: TableShape, seatCount: number) => void
  updateTablePosition: (id: string, x: number, y: number) => void
  updateTablePositions: (updates: { id: string; x: number; y: number }[]) => void
  updateTableTransform: (id: string, patch: TableTransform) => void
  setTableSeatCount: (id: string, seatCount: number) => void
  setTableShape: (id: string, shape: TableShape) => void
  setTableLabel: (id: string, label: string) => void
  deleteTable: (id: string) => void

  // replace the whole plan (e.g. when importing a saved .json file)
  importPlan: (rooms: Room[], tables: FloorTable[]) => void

  resetAll: () => void
}

function buildInitialState() {
  return {
    rooms: DEFAULT_ROOMS,
    tables: [] as FloorTable[],
    activeRoomId: DEFAULT_ROOMS[0]?.id ?? null
  }
}

export const useFloorPlanStore = create<FloorPlanState>()(
  persist(
    (set, get) => {
      // Snapshot the current data into the undo stack and drop the redo stack.
      // Call this at the start of every mutating action, after its guards.
      const pushHistory = () => {
        const { rooms, tables, activeRoomId, past } = get()
        set({
          past: [...past, { rooms, tables, activeRoomId }].slice(-HISTORY_LIMIT),
          future: []
        })
      }

      const defaultView = () => ({ pan: { x: 0, y: 0 }, zoom: 1 })

      // Save the outgoing room's pan/zoom and restore the incoming room's view
      // (or a fresh default if it hasn't been visited yet). Returns the partial
      // state to apply.
      const switchRoomView = (nextRoomId: string | null) => {
        const { activeRoomId, pan, zoom, viewByRoom } = get()
        const nextViews = { ...viewByRoom }
        if (activeRoomId) nextViews[activeRoomId] = { pan, zoom }
        const restored =
          (nextRoomId && nextViews[nextRoomId]) || defaultView()
        return {
          activeRoomId: nextRoomId,
          viewByRoom: nextViews,
          pan: { ...restored.pan },
          zoom: restored.zoom
        }
      }

      return {
        ...buildInitialState(),
        savedSnapshot: null,
        setSavedSnapshot: snapshot => set({ savedSnapshot: snapshot }),
        snapEnabled: false,
        zoom: 1,
        tool: 'select',
        pan: { x: 0, y: 0 },
        viewByRoom: {},
        canvasWidth: 0,
        canvasHeight: 0,
        past: [],
        future: [],

        setTool: tool => set({ tool }),

        setPan: pan => set({ pan }),

        // Centre the view on the active room's tables (and zoom to fit them).
        fitToTables: () => {
          const { tables, activeRoomId, canvasWidth, canvasHeight } = get()
          const roomTables = tables.filter(t => t.roomId === activeRoomId)
          /*
            Nothing to fit, or the canvas has not reported its size yet.

            This used to fall through to `pan 0,0 / zoom 1`, which is a real
            view and therefore indistinguishable from a fit that worked — every
            room got the same 100% at the origin, so the button looked like it
            was centring on some other room. Doing nothing is honest: there is
            no answer to "frame these tables" when there are none, or when the
            frame's size is unknown.
          */
          if (!roomTables.length || !canvasWidth || !canvasHeight) return

          let minX = Infinity
          let minY = Infinity
          let maxX = -Infinity
          let maxY = -Infinity
          roomTables.forEach(table => {
            minX = Math.min(minX, table.x)
            minY = Math.min(minY, table.y)
            maxX = Math.max(maxX, table.x + table.width)
            maxY = Math.max(maxY, table.y + table.height)
          })

          const padding = 80
          const boxWidth = maxX - minX + padding * 2
          const boxHeight = maxY - minY + padding * 2
          const scale = Math.min(
            ZOOM_MAX,
            Math.max(
              ZOOM_MIN,
              Math.min(canvasWidth / boxWidth, canvasHeight / boxHeight)
            )
          )
          const centerX = (minX + maxX) / 2
          const centerY = (minY + maxY) / 2
          const pan = {
            x: canvasWidth / 2 - centerX * scale,
            y: canvasHeight / 2 - centerY * scale
          }
          /*
            Recorded against the room straight away, not only when the room is
            switched away from: a fit that is immediately followed by a reload
            or an import would otherwise be lost, and the room would come back
            at whatever the previous view was.
          */
          set(state => ({
            zoom: scale,
            pan,
            viewByRoom: activeRoomId
              ? { ...state.viewByRoom, [activeRoomId]: { pan, zoom: scale } }
              : state.viewByRoom
          }))
        },

        setCanvasSize: (width, height) => set({ canvasWidth: width, canvasHeight: height }),

        undo: () => {
          const { past, future, rooms, tables, activeRoomId } = get()
          if (past.length === 0) return
          const previous = past[past.length - 1]
          set({
            ...previous,
            past: past.slice(0, -1),
            future: [...future, { rooms, tables, activeRoomId }]
          })
        },

        redo: () => {
          const { past, future, rooms, tables, activeRoomId } = get()
          if (future.length === 0) return
          const next = future[future.length - 1]
          set({
            ...next,
            past: [...past, { rooms, tables, activeRoomId }],
            future: future.slice(0, -1)
          })
        },

        toggleSnap: () => set(state => ({ snapEnabled: !state.snapEnabled })),

        setZoom: zoom =>
          set({ zoom: Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom)) }),

        addRoom: name => {
          const trimmed = name.trim()
          if (!trimmed) return
          pushHistory()
          const { rooms } = get()
          const newRoom: Room = {
            id: `room-${crypto.randomUUID()}`,
            name: trimmed,
            order: rooms.length
          }
          // Save the current room's view and open the new room with a fresh one.
          set({ rooms: [...rooms, newRoom], ...switchRoomView(newRoom.id) })
        },

        renameRoom: (id, name) => {
          const trimmed = name.trim()
          if (!trimmed) return
          pushHistory()
          set(state => ({
            rooms: state.rooms.map(room =>
              room.id === id ? { ...room, name: trimmed } : room
            )
          }))
        },

        deleteRoom: id => {
          const { rooms } = get()
          if (rooms.length <= 1) return // never delete the last room
          pushHistory()
          const remaining = rooms.filter(room => room.id !== id)
          set(state => {
            const nextViews = { ...state.viewByRoom }
            delete nextViews[id]
            const baseline = {
              rooms: remaining,
              tables: state.tables.filter(table => table.roomId !== id),
              viewByRoom: nextViews
            }
            // Deleting a non-active room keeps the current view; deleting the
            // active one switches to the first remaining room's saved view.
            if (state.activeRoomId !== id) return baseline
            const nextActiveId = remaining[0]?.id ?? null
            const restored =
              (nextActiveId && nextViews[nextActiveId]) || defaultView()
            return {
              ...baseline,
              activeRoomId: nextActiveId,
              pan: { ...restored.pan },
              zoom: restored.zoom
            }
          })
        },

        setActiveRoom: id => {
          if (id === get().activeRoomId) return
          set(switchRoomView(id))
        },

        addTable: (shape, seatCount) => {
          const { activeRoomId, tables, canvasWidth } = get()
          if (!activeRoomId) return
          pushHistory()
          const label = getNextTableLabel(tables)
          const { width, height } = DEFAULT_DIMENSIONS[shape]
          const position = findFreeTablePosition(
            shape,
            seatCount,
            width,
            height,
            tables.filter(t => t.roomId === activeRoomId),
            canvasWidth
          )
          const table = createTable(
            activeRoomId,
            shape,
            seatCount,
            label,
            position
          )
          set({ tables: [...tables, table] })
        },

        updateTablePosition: (id, x, y) => {
          pushHistory()
          set(state => ({
            tables: state.tables.map(table =>
              table.id === id ? { ...table, x, y } : table
            )
          }))
        },

        // Move several tables at once (group drag) as a single undo step.
        updateTablePositions: updates => {
          if (updates.length === 0) return
          pushHistory()
          const byId = new Map(updates.map(u => [u.id, u]))
          set(state => ({
            tables: state.tables.map(table => {
              const update = byId.get(table.id)
              return update ? { ...table, x: update.x, y: update.y } : table
            })
          }))
        },

        updateTableTransform: (id, patch) => {
          pushHistory()
          set(state => ({
            tables: state.tables.map(table =>
              table.id === id ? { ...table, ...patch } : table
            )
          }))
        },

        setTableSeatCount: (id, seatCount) => {
          pushHistory()
          set(state => ({
            tables: state.tables.map(table =>
              table.id === id
                ? { ...table, seatCount: Math.max(0, seatCount) }
                : table
            )
          }))
        },

        setTableShape: (id, shape) => {
          pushHistory()
          set(state => ({
            tables: state.tables.map(table =>
              table.id === id
                ? {
                    ...table,
                    shape,
                    ...getDimensionsForShape(shape, table.width, table.height)
                  }
                : table
            )
          }))
        },

        setTableLabel: (id, label) => {
          pushHistory()
          set(state => ({
            tables: state.tables.map(table =>
              table.id === id ? { ...table, label } : table
            )
          }))
        },

        deleteTable: id => {
          pushHistory()
          set(state => ({
            tables: state.tables.filter(table => table.id !== id)
          }))
        },

        importPlan: (rooms, tables) => {
          if (rooms.length === 0) return
          pushHistory()
          set({
            rooms,
            tables,
            activeRoomId: rooms[0]?.id ?? null,
            viewByRoom: {},
            pan: { x: 0, y: 0 },
            zoom: 1
          })
        },

        resetAll: () =>
          set({
            ...buildInitialState(),
            viewByRoom: {},
            pan: { x: 0, y: 0 },
            zoom: 1,
            past: [],
            future: []
          })
      }
    },
    {
      name: 'floor-plan-storage',
      version: FLOOR_PLAN_SCHEMA_VERSION,
      // Only the data is persisted, undo/redo history resets on reload.
      partialize: state => ({
        rooms: state.rooms,
        tables: state.tables,
        activeRoomId: state.activeRoomId,
        snapEnabled: state.snapEnabled,
        zoom: state.zoom
      }),
      // Heal legacy tables whose dimensions don't match their shape (e.g. a
      // 'rectangle' persisted as 90x90 because old code didn't resize on a
      // shape change). Runs once when the stored version is older.
      migrate: persisted => {
        const state = persisted as Partial<FloorPlanState> | undefined
        if (state?.tables) {
          state.tables = state.tables.map(table => ({
            ...table,
            ...getDimensionsForShape(table.shape, table.width, table.height),
            // Labels are now plain numbers, drop any legacy "Nr " prefix.
            label: table.label.replace(/^\s*nr\s*/i, '')
          }))
        }
        return state as FloorPlanState
      }
    }
  )
)
